import { i18next } from '@multaqa/i18n'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addSupplierContact,
  attachSupplierTag,
  createSupplier,
  deactivateSupplier,
  detachSupplierTag,
  fetchSupplier,
  fetchSuppliers,
  fetchTags,
  removeSupplierContact,
  type StoreContactRequest,
  type StoreSupplierRequest,
  type SupplierIndexParams,
  type UpdateSupplierRequest,
  updateSupplier,
} from '../api'

export const supplierKeys = {
  all: ['suppliers'] as const,
  lists: () => [...supplierKeys.all, 'list'] as const,
  list: (params?: SupplierIndexParams) => [...supplierKeys.lists(), params] as const,
  details: () => [...supplierKeys.all, 'detail'] as const,
  detail: (id: string) => [...supplierKeys.details(), id] as const,
  tags: ['tags'] as const,
}

export function useSuppliers(params?: SupplierIndexParams, enabled = true) {
  return useQuery({
    queryKey: supplierKeys.list(params),
    queryFn: () => fetchSuppliers(params),
    enabled,
  })
}

export function useSupplier(id: string) {
  return useQuery({
    queryKey: supplierKeys.detail(id),
    queryFn: () => fetchSupplier(id),
    enabled: Boolean(id),
  })
}

export function useCreateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: StoreSupplierRequest) => createSupplier(data),
    // The supplier form shows every refused save above its fields.
    meta: { inlineError: () => true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.all })
      toast.success(i18next.t('supplier.createSuccess'))
    },
  })
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierRequest }) =>
      updateSupplier(id, data),
    // The supplier form shows every refused save above its fields.
    meta: { inlineError: () => true },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.all })
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(variables.id) })
      toast.success(i18next.t('supplier.updateSuccess'))
    },
  })
}

export function useDeactivateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deactivateSupplier(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.all })
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(id) })
      toast.success(i18next.t('supplier.deactivateSuccess'))
    },
  })
}

/**
 * Bring a deactivated record back into use.
 *
 * There is no `activate` endpoint — deactivation is a POST, reactivation is
 * the ordinary update with `is_active`. Without this the state was one-way
 * from the UI: the only route back was Edit, and a checkbox at the bottom of
 * a long form is not where anyone looks for it.
 */
export function useReactivateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => updateSupplier(id, { is_active: true }),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.all })
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(id) })
      toast.success(i18next.t('supplier.activateSuccess'))
    },
  })
}

export function useAddSupplierContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ supplierId, data }: { supplierId: string; data: StoreContactRequest }) =>
      addSupplierContact(supplierId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(variables.supplierId) })
      toast.success(i18next.t('supplier.contactAdded'))
    },
  })
}

export function useRemoveSupplierContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ supplierId, contactId }: { supplierId: string; contactId: string }) =>
      removeSupplierContact(supplierId, contactId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(variables.supplierId) })
      toast.success(i18next.t('supplier.contactRemoved'))
    },
  })
}

export function useAttachSupplierTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ supplierId, tagId }: { supplierId: string; tagId: string }) =>
      attachSupplierTag(supplierId, tagId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(variables.supplierId) })
      toast.success(i18next.t('supplier.tagAttached'))
    },
  })
}

export function useDetachSupplierTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ supplierId, tagId }: { supplierId: string; tagId: string }) =>
      detachSupplierTag(supplierId, tagId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: supplierKeys.detail(variables.supplierId) })
      toast.success(i18next.t('supplier.tagDetached'))
    },
  })
}

export function useTags() {
  return useQuery({
    queryKey: supplierKeys.tags,
    queryFn: () => fetchTags(),
  })
}
